import moment from 'moment';
import { DateTime } from "luxon";
import numeral from 'numeral';
import { List as ImmutableList, Set as ImmutableSet } from 'immutable';
import { mkSplitItem } from 'data/transactions/transformers';
import { isAcme } from 'isAcme';
import { memoizeWithKey } from 'utils/memoizeWithKey';

import { isSplitTxn, isTransferTxn } from 'data/transactions/utilsNoDependencies';
import { isUnsyncedTransferCoa, isLoanAcctTransferCoa, isSupportedTransferCoa,
  isInvestmentAcctTransferCoa, isL1TransferCoa } from 'data/chartOfAccounts/utils';
import { getCurrencyString } from '../accounts/retrievers';

export * from 'data/transactions/utilsNoDependencies';

//
// given an immutable list of transactions, will return the currency symbol appropriate to represent
// the group.  Either the one shared currency, or "N/A" if they differ
//
// pass in an array of transaction arrays (support for multiple transaction groups), if you only have
// one transaction list, pass in [txList]
//
export function getSharedCurrency(txns, accounts) {

  let currency = null;
  if (txns && accounts && accounts.size > 0) {
    txns.forEach((txnList) => {
      txnList.forEach((x) => {
        const acctRec = accounts.get(x.accountId);
        if (acctRec) {
          const tc = acctRec.get('currency');
          if (tc !== currency) {
            currency = (!currency ? tc : 'N/A');
          }
        }
      });
    });
  }
  return currency;
}

/*
 * getTxnStateInfo
 *
 * This is a mapping from txn SOURCE and STATUS to an object that indicates display information for the transaction
 * object returns contains 'entry', which is either 'bank', 'matched', 'manual', or 'scheduled', and a status which is a combination of
 * transaction state and source, and is one of the following:
 * PENDING
 * CLEARED
 * UPCOMING
 * DUE (Due Today)
 * OVERDUE
 */
export function getTxnStateInfo(txn) {

  const retObj = {
    entry: getMatchState(txn) === 'matched' ? 'matched' : 'bank',
    status: txn.state,
  };

  switch(txn.source && txn.source.trim()) {

    case 'QCS_REFRESH':
    case 'QCS_BATCH_REFRESH':
    case 'CLIENT_AGGREGATED':
      break;

    case 'QCS_REFRESH_BANK_PENDING':
    case 'QCS_BATCH_REFRESH_BANK_PENDING':
    case 'REAL_TIME':
      break;

    case 'USER_ENTERED':
      retObj.entry = getMatchState(txn) === 'matched' ? 'matched' : 'manual';
      if (txn.state === 'CLEARED' || txn.state === 'RECONCILED') {
        retObj.status = txn.state;
      } else {
        retObj.status = moment(txn.postedOn).isAfter(moment(), 'day') ? 'UPCOMING' : 'PENDING';
      }
      break;

    case 'USER_OWNED_BANK_PENDING':
    case 'USER_OWNED_REAL_TIME_BANK_PENDING':
      break;

    case 'SCHEDULED_TRANSACTION_PENDING':
      // retObj.entry = 'manual';
      retObj.status = moment(txn.postedOn).isAfter(moment(), 'day') ? 'UPCOMING' : 'OVERDUE';
      if (moment(txn.postedOn).isSame(moment(), 'day')) {
        retObj.status = 'DUE';
      }
      retObj.entry = (retObj.entry === 'matched') ? 'matched' : 'scheduled';
      break;

    case 'SCHEDULED_TRANSACTION_UNKNOWN':
    case 'SCHEDULED_TRANSACTION': {
      if (txn.state === 'PENDING') {
        retObj.status = moment(txn.postedOn).isAfter(moment(), 'day') ? 'UPCOMING' : 'PENDING';
      } else {
        retObj.status = 'CLEARED';
      }
      retObj.entry = (retObj.entry === 'matched') ? 'matched' : 'scheduled';
      break;
    }

    case 'SCHEDULED_TRANSACTION_SKIPPED':
      // not applicable, this transaction should not be shown (soft deleted)
      break;

    case 'UNKNOWN':
      // should never happen - throw an event?
      retObj.entry = 'manual';
      retObj.status = moment(txn.postedOn).isAfter(moment(), 'day') ? 'UPCOMING' : 'CLEARED';
      break;

    default:
      // this should REALLY never happen
      break;

  }

  return retObj;
}




function currenciesAreValidAndDifferent(a, b) {
  if (a === 'N/A' || b === 'N/A') {
    return false;
  }
  return (a !== b);
}

// -------------------------------------------------------
// setReportsFlags
export function setReportsFlags(txn, value) {
  return txn && txn.set('isExcludedFromReports', Boolean(value)).set('isExcludedFromF2S', Boolean(value));
}

// -------------------------------------------------------
// isMultiCurrencyTransferTxn
export function isMultiCurrencyTransferTxn(txn, state) {

  let isMultiCurrency = false;
  const txnCurrency = getCurrencyString(txn.accountId, state);

  if (isTransferTxn(txn)) {
    if (isSplitTxn(txn)) {
      txn.split.items.forEach((item) => {
        if (item.coa.type === 'ACCOUNT') {
          isMultiCurrency = isMultiCurrency || currenciesAreValidAndDifferent(getCurrencyString(item.coa.id, state), txnCurrency);
        }
      });
    } else {
      isMultiCurrency = currenciesAreValidAndDifferent(getCurrencyString(txn.coa.id, state), txnCurrency);
    }
  }
  return isMultiCurrency;
}

const pendingStates = [
  'QCS_REFRESH_BANK_PENDING',
  'QCS_BATCH_REFRESH_BANK_PENDING',
  'USER_OWNED_BANK_PENDING',
  'REAL_TIME',
  'USER_OWNED_REAL_TIME_BANK_PENDING',
];
export function isBankPendingTxn(txn) {
  return pendingStates.indexOf(txn.source) >= 0;
}

export function isBankDownloadedTxn(txn) {
  return txn && txn.cpData;
}

export function isPendingTxn(txn) {
  return txn && txn.state === 'PENDING';
}

export function isActionableScheduledInstance(txn, scheduledTransactionsById) {
  const stateInfo = getTxnStateInfo(txn);
  const status = { stateInfo };
  return ((status === 'DUE' || status === 'OVERDUE') || isNextScheduledInstance(txn, scheduledTransactionsById));
}

export function isNextScheduledInstance(txn, scheduledTransactionsById) {
  if (isUnacceptedScheduledTxn(txn)) {
    const model = scheduledTransactionsById.get(txn.stModelId);
    return (model && (moment(model.dueOn).format('MM/DD/YYYY') === moment(txn.stDueOn).format('MM/DD/YYYY')));
  }
  return false;
}

function sortedScheduledInstancesForAccount(accountTransactions, modelId) {
  let scheduledInstances = accountTransactions.filter((x) => isUnacceptedScheduledTxn(x) && modelId === x.stModelId);
  if (scheduledInstances) {
    scheduledInstances = scheduledInstances.toList();
    scheduledInstances = scheduledInstances.sort((a, b) =>
      a.postedOnAsUnix - b.postedOnAsUnix);
  }
  return scheduledInstances;
}

// let memoCache = [];

export function nextScheduledInstanceAfterDate(date, accountTransactions, modelId) {

  const key = `${moment(date).format('YYYY-MM-DD')}-${modelId}-${accountTransactions.hashCode()}`;
  return memoizeWithKey(
    'nextScheduled',
    () => internalNextScheduledInstanceAfterDate(date, accountTransactions, modelId),
    key,
    50,
  );

  /*
  const index = memoCache.findIndex((x) => x.key === key);
  if (index !== -1) return memoCache[index].value;

  const ret = internalNextScheduledInstanceAfterDate(date, accountTransactions, modelId);
  memoCache.push({key, value: ret});
  if (memoCache.size > 50) {
    memoCache = memoCache.slice(1);
  }
  return ret;
   */
}

function internalNextScheduledInstanceAfterDate(date, accountTransactions, modelId) {

  const scheduledInstances = sortedScheduledInstancesForAccount(accountTransactions, modelId);
  const index = scheduledInstances.findIndex((x) => moment(x.postedOn).isAfter(moment(date)));
  if (index !== -1) {
    return scheduledInstances.get(index);
  }
  return null;
}

export function nextScheduledInstanceAfterTransaction(txn, accountTransactions) {
  const scheduledInstances = sortedScheduledInstancesForAccount(accountTransactions, txn.stModelId);
  const index = scheduledInstances.findIndex((x) => x.id === txn.id);
  if (index !== -1 && (index + 1) < scheduledInstances.size) {
    return scheduledInstances.get(index + 1);
  }
  return null;
}

export const isAcceptedScheduledTxn = (txn) => txn.source && txn.source === 'SCHEDULED_TRANSACTION';
export const isUnacceptedScheduledTxn = (txn) => txn.source && txn.source === 'SCHEDULED_TRANSACTION_PENDING';

export const isGoalTxnInNonGoalAccount = (txn, goalAccountIds) =>
  txn.coa && txn.coa.type === 'ACCOUNT' && goalAccountIds.contains(txn.coa.id) && !goalAccountIds.contains(txn.accountId);


// -------------------------------------------------------
// balanceSplitTxn
// made to work with JS objects and immutables
export function balanceSplitTxn(txn) {

  let newTxn = txn;
  if (isUnbalancedSplitTxn(txn)) {
    const isImmutable = txn.split.items.size !== undefined;

    const diff = Number(txn.amount) - sumSplitItems(txn.split);
    const uncatItem = txn.split.items.findIndex((item) => item.coa.type === 'UNCATEGORIZED');

    // if there is an uncategorized row already, add the difference to it
    if (uncatItem >= 0) {
      const newAmount =
        Number(isImmutable ? txn.split.items.get(uncatItem).amount : txn.split.items[uncatItem].amount) + diff;

      if (isImmutable) {
        newTxn = txn.setIn(['split', 'items', uncatItem, 'amount'], newAmount);
      } else {
        newTxn.split.items[uncatItem].amount = newAmount;
      }
    // otherwise create a new uncategorized row
    } else if (isImmutable) {
      let { items } = txn.split;
      items = items.push(mkSplitItem({ amount: diff, coa: { type: 'UNCATEGORIZED', id: '0' }}));
      newTxn = txn.setIn(['split', 'items'], items);
    } else {
      newTxn.split.items.push({ coa: { type: 'UNCATEGORIZED', id: '0' }, amount: diff });
    }
  }
  return newTxn;
}

// -------------------------------------------------------
// isUnbalancedSplitTxn
// made to work with JS objects and immutables
export function isUnbalancedSplitTxn(txn) {

  if (isSplitTxn(txn)) {
    return Number(txn.amount).toFixed(2) !== sumSplitItems(txn.split).toFixed(2);
  }
  return false;
}

export function sumSplitItems(split) {
  let sum = 0;
  if (split && split.items) {
    split.items.forEach((item) => {
      sum += Number(item.amount);
    });
  }
  return sum;
}

// -------------------------------------------------------
// isUncategorizedTxn
export function isUncategorizedTxn(txn) {
  return (!isSplitTxn(txn) && (!txn.coa || txn.coa.type === 'UNCATEGORIZED'));
}

// -------------------------------------------------------
// txnHasTags
export function txnHasTags(txn) {
  return Boolean(txn.tags && txn.tags.size > 0);
}

// -------------------------------------------------------
// isSupportedTransferTxn
//
export function isSupportedTransferTxn(txn, state) {
  if (isSplitTxn(txn)) {
    return Boolean(txn.split.items.find((item) => isSupportedTransferCoa(item.coa, state)));
  }
  return isSupportedTransferCoa(txn.coa, state);
}

// -------------------------------------------------------
// isUnsyncedTransferTxn
//
export function isUnsyncedTransferTxn(txn, state) {

  if (isSplitTxn(txn)) {
    return Boolean(txn.split.items.find((item) => isUnsyncedTransferCoa(item.coa, state)));
  }
  return isUnsyncedTransferCoa(txn.coa, state);
}

// -------------------------------------------------------
// isLoanAcctTransferTxn
//
export function isLoanAcctTransferTxn(txn, state) {

  if (isSplitTxn(txn)) {
    return Boolean(txn.split.items.find((item) => isLoanAcctTransferCoa(item.coa, state)));
  }
  return isLoanAcctTransferCoa(txn.coa, state);
}

// -------------------------------------------------------
// isInvestmentAcctTransferTxn
//
export function isInvestmentAcctTransferTxn(txn, state) {

  if (isSplitTxn(txn)) {
    return Boolean(txn.split.items.find((item) => isInvestmentAcctTransferCoa(item.coa, state)));
  }
  return isInvestmentAcctTransferCoa(txn.coa, state);
}

// -------------------------------------------------------
// Returns null if edits are allowed, otherwise returns
// a reason code
// UNSUPPORTED_SCHED_TRANSFER
// BILLPAY_TRANSACTION
//
export function transactionEditsNotAllowed(txn, state) {

  if (isAcme) {
    return null;
  }
  // if this is a transfer, and a scheduled instance make sure it is a legit transfer
  if (isTransferTxn(txn) && isUnacceptedScheduledTxn(txn, state) && !isSupportedTransferTxn(txn, state)) {
    return 'UNSUPPORTED_SCHED_TRANSFER';
  }
  if (txn.paymentProvider === 'OFX') {
    return 'BILLPAY_TRANSACTION';
  }
  return null;
}

//-----------------------------------------------------------------------
//
// getMatchState
//
// For any transaction will return the following
//
// 'manual' - the transaction has no cpData
// 'matched' - the transaction has cpData and is matchstate is one of the MATCHED states
// 'downloaded' - the transaction has cpData, is not matched, and is not reviewed
//
export function getMatchState(txn) {
  if (txn.cpData) {
    if ((txn.matchState === 'AUTO_MATCHED') || (txn.matchState === 'USER_MATCHED')) {
      return 'matched';
    }
    return ('downloaded');
  }
  return ('manual');
}

export function isBankPending(txn) {
  return isBankPendingTxn(txn); // /.+_BANK_PENDING$/.test(txn.source);
}

export function isUserOwned(txn) {
  // return (txn.source.indexOf('USER_') === 0 || txn.source.indexOf('SCHEDULED_TRANSACTION') === 0);
  return /^USER_.+/.test(txn.source) || /^SCHEDULED_TRANSACTIO.+/.test(txn.source);
}

// A "match" is one which has a user-owned txn matched to a posted-txn. This is specifically excluding a
// transactions that are user-owned and matched to bank-pending txn.
export function isMatchedToPostedTxn(txn) {
  return isUserOwned(txn) && txn.cpData && txn.cpData.id != null && txn.matchedTxnId != null;
}

export function isAssociatedWithBankPendingTxn(txn) {
  return txn.cpData != null && txn.cpData.pendingId != null;
}

export function areMatchable(txn1, txn2) {
  let srcTxn;
  let dstTxn;

  // console.log("ARE MATCHABLE ", txn1, txn2);

  if (isUserOwned(txn1)) {
    if (isUserOwned(txn2)) {
      return false;  // one must be non user-owned
    }
    srcTxn = txn2;
    dstTxn = txn1;
  } else {
    if (!isUserOwned(txn2)) {
      return false;  // one must be user-owned
    }
    srcTxn = txn1;
    dstTxn = txn2;
  }
  if (srcTxn.accountId !== dstTxn.accountId) {
    return false;  // they must belong to the same account
  }
  if (isMatchedToPostedTxn(dstTxn)) {
    return false;  // already matched to posted-txn (need to first unmatch before re-matching)
  }
  if (isBankPending(srcTxn) && isAssociatedWithBankPendingTxn(dstTxn)) {
    return false;  // destination txn is already associated with a bank-pendihng txn
  }
  return true;
}



// When entering a new transfer transaction, looks for an already existing transaction that may
// be a possible match for user prompting
//
export function addPossibleMatch(match, baseTxn, xfrTxn, allMatches, forceWarn = false) {
  const possibleMatch = match.set('coa', { id: baseTxn.accountId, type: 'ACCOUNT' });
  allMatches.push({ matchingNewTxn: xfrTxn, possibleMatch, baseTxn, forceWarn });
}

//
// Given a user provided amount, proposedSign is either 1 or -1
//
export function quickenAmountToString(amountStr, proposedSign = 1) {

  // this just safety checks the value to ensure it is either 1 or -1
  let sign = ((proposedSign / proposedSign || 1) * Math.sign(proposedSign)) || 1;

  if (String(amountStr).charAt(0) === '+') {
    sign = 1;
  }
  if (String(amountStr).charAt(0) === '-') {
    sign = -1;
  }
  return numeral(Math.abs(Number(amountStr)) * sign).format('0.00');
}


//-------------------------------------------------------
// findTransaction
//
export function findTransaction(txnsByAccountId, txnId, accountId) {

  let ret = null;
  if (accountId) {
    const acctTxns = txnsByAccountId.get(accountId);
    if (acctTxns) {
      ret = acctTxns.get(txnId);
    }
  }

  // slow way
  if (!ret) {
    txnsByAccountId.some((txnList) =>
      txnList.some((tx) => {
        if (tx.id === txnId) {
          ret = tx;
          return true;
        }
        return false;
      }));
  }

  return ret;
}

export function getTxnDifferences(txn, txnPrev) {

  const flds = ['accountId', 'postedOn', 'payee', 'coa', 'amount', 'userFlag',
    'memo', 'state', 'isReviewed', 'tags', 'check', 'attachments', 'split', 'isExcludedFromReports'];
  const changed = [];
  flds.forEach((fld) => {
    if (fieldChanged(txn, txnPrev, fld)) {
      changed.push(fld);
    }
  });
  return changed;
}

export function fieldChanged(txn, txnPrev, field) {

  // this is checking if one or the other is null, however, we have to do a bit more
  // for splits since even though .split may not be null, it can still be a non-split if
  // there are no items

  // should not be called with either value being null
  if (!txn || !txnPrev) {
    return false;
  }

  if (!txn[field] || !txnPrev[field]) {
    return field === 'split' ?
      isSplitTxn(txn) !== isSplitTxn(txnPrev) :
      Boolean(txn[field]) !== Boolean(txnPrev[field]);
  }
  switch (field) {

    case 'amount': {
      return (Number(txnPrev.amount) !== Number(txn.amount));
    }

    case 'postedOn': {
      return (!moment(txnPrev.postedOn).isSame(moment(txn.postedOn), 'day'));
    }

    case 'coa': {
      return (txnPrev.coa.id !== txn.coa.id);
    }

    case 'check': {
      return (txnPrev.check.number !== txn.check.number);
    }

    case 'attachments': {
      const sum1 = txn[field].sort((a, b) => a > b ? 1 : -1).reduce((total, num) => `${total}-${num}`, '');
      const sum2 = txnPrev[field].sort((a, b) => a > b ? 1 : -1).reduce((total, num) => `${total}-${num}`, '');
      return sum1 !== sum2;
    }

    case 'tags': {
      return !areTagsEqual(txn[field], txnPrev[field]);
    }

    case 'split': {
      if (isSplitTxn(txn) !== isSplitTxn(txnPrev)) {
        return true;
      }
      const sum1 = txnPrev.split.items.sort((a, b) =>
        a.coa.id > b.coa.id ? 1 : -1).reduce((total, item) =>
        `${total}-${splitStringVal(item)}`, '');

      const sum2 = txn.split.items.sort((a, b) =>
        a.coa.id > b.coa.id ? 1 : -1).reduce((total, item) =>
        `${total}-${splitStringVal(item)}`, '');

      return sum1 !== sum2;
    }
    default:
      if (txnPrev) {
        return txn[field] !== txnPrev[field];
      }
  }
  return false;
}

function splitStringVal(item) {
  return `${item.coa.id}-${Number(item.amount).toFixed(2)}-${item.memo}-${item.tags ? item.tags.length : 'none'}`;
}

export function areTagsEqual(tags1, tags2) {
  if (!tags1 && !tags2) {
    return true;
  }
  if (!tags1 || !tags2) {
    return false;
  }
  const sum1 = tags1.sort((a, b) => a.id > b.id ? 1 : -1).reduce((total, num) =>
    `${total}-${num.id}`, '');
  const sum2 = tags2.sort((a, b) => a.id > b.id ? 1 : -1).reduce((total, num) =>
    `${total}-${num.id}`, '');
  return sum1 === sum2;
}


// The code below is used to find possible transfers for new transactions being inserted.  When a new
// transfer transaction is entered, we look in the transfer account for a possible transaction match that
// may already be there, and offer the user to match to that transaction rather than create a dup
//
function isPossibleMatch(txn, postedOn, amount, days = 31) {

  const diff = moment(postedOn).diff(moment(txn.postedOn), 'days');

  return !isSplitTxn(txn) && !isTransferTxn(txn) && (Math.abs(diff) <= days) && (Number(amount) === Number(txn.amount));
}

export function sortTxnsByDateDifference(txns, referenceDate) {

  return txns.sort((a, b) => {
    const diff1 = Math.abs(moment(a.postedOn).diff(moment(referenceDate), 'days'));
    const diff2 = Math.abs(moment(b.postedOn).diff(moment(referenceDate), 'days'));
    if (diff1 === diff2) {
      return 0;
    }
    if (diff1 > diff2) {
      return 1;
    }
    return -1;
  });
}

export function findPossibleTransferCandidate(accountId, postedOn, amount, txnsByAccountId, days) {

  const acctTxns = txnsByAccountId.get(accountId) || new ImmutableList([]);
  const txns = acctTxns.filter((tx) => isPossibleMatch(tx, postedOn, amount, days)).toList();
  if (txns) {
    return sortTxnsByDateDifference(txns, postedOn).first();
  }
  return null;
}

export function possiblyMatchTransfer(_txn, txnsByAccountId) {

  let txn = _txn;

  // console.log("Checking for L1 Transfer... ", txn.coa, isL1TransferCoa(txn.coa));
  if (!isSplitTxn(txn) && !isTransferTxn(txn) && isL1TransferCoa(txn.coa)) {
    let matches = new ImmutableList();
    txnsByAccountId.forEach((acctTxns, acctId) => {
      if (acctId !== txn.accountId) {
        // check if there is a matching transaction and score it
        const match = findPossibleTransferCandidate(acctId, txn.postedOn, -txn.amount, txnsByAccountId, 5);
        if (match) {
          matches = matches.push(match);
        }
      }
    })
    if (matches.size > 0) {
      // console.log("ALL POSSIBLE MATCHES ARE ", matches.toJS());
      const bestMatch = sortTxnsByDateDifference(matches, txn.postedOn).first();

      txn = txn.set('coa', { type: 'ACCOUNT', id: bestMatch.accountId});
    }
  }
  return txn;
}

export function coasAreDifferent(coa1, coa2) {
  if (!coa1 && !coa2) {
    return false;
  }
  if (!coa1 || !coa2) {
    return true;
  }
  return (coa1.type !== coa2.type) || (coa1.id !== coa2.id);
}

export function transactionsSortFunction(a, b) {

  // note, these are day boundary values constructed by convertToUnixDay
  let cmpValue = Number(a.postedOnAsUnix) - Number(b.postedOnAsUnix);

  if (cmpValue === 0) {
    // we sort balance adjustments to the top
    if ((a.coa && a.coa.type === 'BALANCE_ADJUSTMENT') || b.id.substr(0, 3) === 'new') {
      cmpValue = -1;
    } else if (b.coa && b.coa.type === 'BALANCE_ADJUSTMENT' || a.id.substr(0, 3) === 'new') {
      cmpValue = 1;
    } else {
      const va = Number(a.id) || Number(b.id) + 1;
      const vb = Number(b.id) || Number(a.id) + 1;
      cmpValue = va - vb;
    }
  }
  return cmpValue;
}

// TODO Need UNIT TESTS for this potentially data damaging functionality

// determine if this transaction has the given tag id in it
export function txnHasTag(txn, tag) {
  if (isSplitTxn(txn)) {
    const ret = txn.split.items.find((item) =>
      item.tags && item.tags.find((rec) => rec.id === tag.id));
    return Boolean(ret);
  }
  return (txn.tags && txn.tags.filter((t) => t.id === tag.id).size > 0);
}

// remove the reference to the given tag id from the transaction
export function removeTxnTag(txn, tag) {
  if (isSplitTxn(txn)) {
    return txn.setIn(['split', 'items'], txn.split.items.map((item) => item.tags ? item.set('tags', item.tags.filter((t) => t.id !== tag.id)) : item));
  }
  return txn.set('tags', txn.tags.filter((t) => t.id !== tag.id));
}

// simple protected dateTime check
export function dateTimeFromTxn(txn) {
  return (txn.postedOn && DateTime.fromISO(txn.postedOn) || txn.stDueOn && DateTime.fromISO(txn.stDueOn) || DateTime.local()).startOf('day');
}

// used for income/bill type abstraction based on category and isBill/Subscription flags
export function isIncomeFromTxn(txn, incomeCOAs) {
  if (!txn) return false;
  if (txn.isSubscription || txn.isBill) return false;
  if (txn.coa) {
    return incomeCOAs.has(txn.coa.id)
  }
  if (isSplitTxn(txn)) { // split
    return txn.split.items && Boolean(txn.split.items.find((item) => item.coa && item.coa.id && incomeCOAs.has(item.coa.id)));
  }
  return false;
}

/*
 * deDupeTxns
 *
 * Given a immutable list of transactions, will identify duplicates by txnId, and remove them so the returned list
 * contains only unique transactions
 */
export function deDupeTxns(txns) {
  let exclusionSet = new ImmutableSet();
  return txns.filter((txn) => {
    const dup = exclusionSet.includes(txn.id);
    exclusionSet = exclusionSet.add(txn.id);
    return !dup;
  });
}
